import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Card,
  CardHeader,
  VStack,
  CardBody,
  Table,
  Th,
  Tr,
  Thead,
  Tbody,
  Td,
} from '@chakra-ui/react';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  InvoiceCalculationComparisonFragmentGQLType,
  InvoiceCreatedByTypeGQLType,
  InvoiceDisbursementItemDifferenceFragmentGQLType,
  InvoiceFragmentGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommasOrDash,
  numberToPercentageLabel,
} from 'packages/common-frontend/utils';
import { InvoiceDetailActionsMenu } from 'packages/invoices/ui/invoiceDetail/InvoiceDetailActionsMenu';
import { getLoanLabel } from 'packages/invoices/ui/utils';

const GlobalAccruedInterestComparisonTable = ({
  invoiceComponentDifferences,
  invoiceCreatedByType,
}: {
  invoiceComponentDifferences: InvoiceCalculationComparisonFragmentGQLType['invoiceComponentDifferences'];
  invoiceCreatedByType: InvoiceCreatedByTypeGQLType;
}) => {
  return (
    <>
      <Typography variant="base" fontWeight="bold">
        Global Accrued Interest
      </Typography>
      <Table mt="1rem" border="1px solid" borderColor="gray.200">
        <Thead>
          <Tr>
            <Th w="25%" />
            <Th textAlign="right" w="25%">
              <Typography variant="base" fontWeight="semibold">
                {invoiceCreatedByType === 'ibs' ? 'IBS Amount' : 'Input Amount'}
              </Typography>
            </Th>
            <Th textAlign="right" w="25%">
              <Typography variant="base" fontWeight="semibold">
                Finley Amount
              </Typography>
            </Th>
            <Th textAlign="right" w="25%">
              <Typography variant="base" fontWeight="semibold">
                Difference
              </Typography>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {invoiceComponentDifferences.map((difference, idx) => {
            const key = idx;
            return (
              <Tr key={key}>
                <Td />
                <Td verticalAlign="top" textAlign="right">
                  <Typography variant="base-muted">
                    {dollarNumberWithCommasOrDash(difference.inputAmount)}
                  </Typography>
                </Td>
                <Td verticalAlign="top" textAlign="right">
                  <Typography variant="base-muted">
                    {dollarNumberWithCommasOrDash(difference.finleyAmount)}
                  </Typography>
                </Td>
                <Td verticalAlign="top" textAlign="right">
                  <Typography variant="base-muted">
                    {dollarNumberWithCommasOrDash(difference.difference)}
                  </Typography>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </>
  );
};

const DisbursementComparisonTable = ({
  disbursementItemDifferences,
  disbursementType,
  invoiceCreatedByType,
}: {
  disbursementItemDifferences: InvoiceDisbursementItemDifferenceFragmentGQLType[];
  disbursementType: 'interest' | 'principal';
  invoiceCreatedByType: InvoiceCreatedByTypeGQLType;
}) => {
  return (
    <>
      <Typography variant="base" fontWeight="bold">
        {commonUtils.toTitleCase(disbursementType)} Disbursement Differences
      </Typography>
      <Table mt="1rem" border="1px solid" borderColor="gray.200">
        <Thead>
          <Tr>
            <Th w="25%">
              <Typography variant="base" fontWeight="semibold">
                Lender
              </Typography>
            </Th>
            <Th textAlign="right" w="25%">
              <Typography variant="base" fontWeight="semibold">
                {invoiceCreatedByType === 'ibs' ? 'IBS Amount' : 'Input Amount'}
              </Typography>
            </Th>
            <Th textAlign="right" w="25%">
              <Typography variant="base" fontWeight="semibold">
                Finley Amount
              </Typography>
            </Th>
            <Th textAlign="right" w="25%">
              <Typography variant="base" fontWeight="semibold">
                Difference
              </Typography>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {disbursementItemDifferences.map((difference, idx) => {
            const key = idx;
            return (
              <Tr key={key}>
                <Td verticalAlign="top">
                  <Typography variant="base-muted">
                    {difference.capitalProviderName}
                  </Typography>
                </Td>
                <Td verticalAlign="top" textAlign="right">
                  <Typography variant="base-muted">
                    {dollarNumberWithCommasOrDash(difference.inputAmount)}
                  </Typography>
                  {difference.inputProRataShare && (
                    <Typography variant="base-muted">
                      {numberToPercentageLabel(difference.inputProRataShare, 6)}
                    </Typography>
                  )}
                </Td>
                <Td verticalAlign="top" textAlign="right">
                  <Typography variant="base-muted">
                    {dollarNumberWithCommasOrDash(difference.finleyAmount)}
                  </Typography>
                  {difference.finleyProRataShare && (
                    <Typography variant="base-muted">
                      {numberToPercentageLabel(
                        difference.finleyProRataShare,
                        6,
                      )}
                    </Typography>
                  )}
                </Td>
                <Td verticalAlign="top" textAlign="right">
                  <Typography variant="base-muted">
                    {dollarNumberWithCommasOrDash(difference.difference)}
                  </Typography>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </>
  );
};

const CalculationComparisonForLoan = ({
  calculationComparison,
  invoiceCreatedByType,
}: {
  calculationComparison: InvoiceCalculationComparisonFragmentGQLType;
  invoiceCreatedByType: InvoiceCreatedByTypeGQLType;
}) => {
  const {
    loanId,
    loanName,
    externalLoanId,
    invoiceComponentDifferences,
    invoiceDisbursementItemDifferences,
  } = calculationComparison;

  return (
    <Card variant="outline" w="100%">
      <CardHeader>
        <Typography variant="base" fontWeight="bold">
          {getLoanLabel({
            loanId,
            loanName,
            externalLoanId: externalLoanId ?? null,
            alwaysIncludeLoanName: true,
          })}
        </Typography>
      </CardHeader>
      <CardBody>
        <VStack align="flex-start" gap="1rem">
          {invoiceComponentDifferences.length > 0 && (
            <GlobalAccruedInterestComparisonTable
              invoiceComponentDifferences={invoiceComponentDifferences}
              invoiceCreatedByType={invoiceCreatedByType}
            />
          )}
          {invoiceDisbursementItemDifferences.interest.length > 0 && (
            <DisbursementComparisonTable
              disbursementItemDifferences={
                invoiceDisbursementItemDifferences.interest
              }
              disbursementType="interest"
              invoiceCreatedByType={invoiceCreatedByType}
            />
          )}
          {invoiceDisbursementItemDifferences.principal.length > 0 && (
            <DisbursementComparisonTable
              disbursementItemDifferences={
                invoiceDisbursementItemDifferences.principal
              }
              disbursementType="principal"
              invoiceCreatedByType={invoiceCreatedByType}
            />
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export const InvoiceCalculationComparisonsSideDrawer = ({
  invoice,
  isOpen,
  onClose,
}: {
  invoice: InvoiceFragmentGQLType;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent p="1rem">
        <DrawerHeader fontSize="1rem">
          <Flex justifyContent="space-between">
            <Typography variant="section-title">
              Comparison of
              {invoice.createdByType === 'ibs' ? ' IBS ' : ' Input '}
              and Finley Calculations
            </Typography>
            <InvoiceDetailActionsMenu
              invoice={invoice}
              isReverseInvoicePaymentLoading={false}
            />
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="flex-start" gap="2rem">
            {invoice.invoiceCalculationComparisons?.map((comparison) => (
              <CalculationComparisonForLoan
                key={comparison.loanId}
                calculationComparison={comparison}
                invoiceCreatedByType={invoice.createdByType}
              />
            ))}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
